
import React from 'react';
import { deriveWorkflowType, getWorkflowMetadata } from '../utils/orchestrationConfigs';

import './FastTrackFlowChart.css'; // Reuse existing CSS styles

/**
 * UnifiedOrchestrationFlowChart component for visualizing orchestration processes
 * 
 * This component replaces both FastTrackFlowChart and SalesforceOrchestrationFlowChart
 * with a unified, configurable approach.
 *
 * @param {Object} props - Component props
 * @param {Object} props.orchestrationData - Data about the current state of the orchestration process
 * @param {Object} props.config - Configuration object defining steps, layout, etc.
 */
const UnifiedOrchestrationFlowChart = ({ orchestrationData, config, message }) => {
  if (!orchestrationData || !config) return null;

  // Get workflow metadata for enhanced functionality
  const workflowMetadata = message ? getWorkflowMetadata(message) : null;

  // Helper function to determine node status
  const getNodeStatus = (nodeType) => {
    const status = orchestrationData[`${nodeType}_status`];

    // If the overall orchestration is stopped, show stopped status for processing nodes
    if (orchestrationData.status === 'stopped' && status === 'processing') {
      return 'stopped';
    }

    if (status === 'processing') return 'processing';
    if (status === 'completed') return 'completed';
    if (status === 'failed') return 'failed';
    if (status === 'stopped') return 'stopped';

    // Check if previous steps are completed to determine if this step is pending or not yet started
    return determinePendingStatus(nodeType, config.steps, orchestrationData);
  };

  // Helper function to determine if a step should be pending or not-started
  const determinePendingStatus = (nodeType, steps, data) => {
    // Handle fast track parallel processing (both fast-track and fast-track-shell)
    if (message && (message.action === 'fast-track' || message.action === 'fast-track-shell')) {
      // Campaign and creative start immediately (parallel processing)
      if (nodeType === 'campaign' || nodeType === 'creative') {
        return 'pending';
      }

      // Launcher waits for both campaign and creative to complete OR be skipped
      if (nodeType === 'launcher') {
        const campaignReady = data.campaign_status === 'completed';
        const creativeReady = data.creative_status === 'completed' || data.creative_status === 'skipped';
        return (campaignReady && creativeReady) ? 'pending' : 'not-started';
      }

      // API waits for launcher to complete
      if (nodeType === 'api') {
        return data.launcher_status === 'completed' ? 'pending' : 'not-started';
      }
    }

    // For sequential flow (salesforce), check if all previous steps are completed
    const currentIndex = steps.indexOf(nodeType);
    if (currentIndex === -1) return 'not-started';

    // Check if all previous steps are completed
    for (let i = 0; i < currentIndex; i++) {
      const prevStepStatus = data[`${steps[i]}_status`];
      if (prevStepStatus !== 'completed') {
        return 'not-started';
      }
    }

    return 'pending';
  };

  // Use flowChartSteps if available, otherwise fall back to regular steps
  const flowSteps = config.flowChartSteps || config.steps;
  const flowLabels = config.flowChartLabels || config.stepLabels;

  // Get status for each step
  const stepStatuses = flowSteps.map(step => ({
    step,
    status: getNodeStatus(step)
  }));

  const finalStatus = orchestrationData.status === 'completed' ? 'completed' :
                      orchestrationData.status === 'failed' ? 'failed' :
                      orchestrationData.status === 'stopped' ? 'stopped' : 'pending';

  // Dynamic layout calculation based on orchestration type
  const nodeWidth = 120;
  const nodeHeight = 60;
  const startX = 50;
  const startY = config.flowChartHeight / 2 - nodeHeight / 2;

  // Calculate positions based on orchestration type
  let positions, completeX, completeY;

  if (message && (message.action === 'fast-track' || message.action === 'fast-track-shell')) {
    // Fast track parallel layout (matches original FastTrackFlowChart)
    const spacing = 160;
    positions = {
      'campaign': { x: startX + spacing, y: startY - 60 },
      'creative': { x: startX + spacing, y: startY + 60 },
      'launcher': { x: startX + spacing * 2, y: startY },
      'api': { x: startX + spacing * 3, y: startY }
    };
    completeX = startX + spacing * 4;
    completeY = startY;
  } else {
    // Sequential layout for salesforce orchestration
    const spacing = 150;
    positions = {};
    stepStatuses.forEach((stepData, index) => {
      positions[stepData.step] = {
        x: startX + (index + 1) * spacing,
        y: startY
      };
    });
    completeX = startX + (stepStatuses.length + 1) * spacing;
    completeY = startY;
  }

  // Generate connecting lines
  const generateLines = () => {
    const lines = [];

    if (message && (message.action === 'fast-track' || message.action === 'fast-track-shell')) {
      // Fast track parallel flow lines (matches original FastTrackFlowChart)
      const campaignStatus = getNodeStatus('campaign');
      const creativeStatus = getNodeStatus('creative');
      const launcherStatus = getNodeStatus('launcher');
      const apiStatus = getNodeStatus('api');

      // Start to campaign and creative (parallel branches)
      lines.push({
        path: `M ${startX + nodeWidth} ${startY + nodeHeight/4} L ${positions.campaign.x} ${positions.campaign.y + nodeHeight/2}`,
        active: campaignStatus !== 'not-started'
      });
      lines.push({
        path: `M ${startX + nodeWidth} ${startY + nodeHeight*3/4} L ${positions.creative.x} ${positions.creative.y + nodeHeight/2}`,
        active: creativeStatus !== 'not-started'
      });

      // Campaign and creative to launcher (convergence)
      lines.push({
        path: `M ${positions.campaign.x + nodeWidth} ${positions.campaign.y + nodeHeight/2} L ${positions.launcher.x} ${positions.launcher.y + nodeHeight/3}`,
        active: campaignStatus === 'completed'
      });
      lines.push({
        path: `M ${positions.creative.x + nodeWidth} ${positions.creative.y + nodeHeight/2} L ${positions.launcher.x} ${positions.launcher.y + nodeHeight*2/3}`,
        active: creativeStatus === 'completed' || creativeStatus === 'skipped'
      });

      // Launcher to API
      lines.push({
        path: `M ${positions.launcher.x + nodeWidth} ${positions.launcher.y + nodeHeight/2} L ${positions.api.x} ${positions.api.y + nodeHeight/2}`,
        active: launcherStatus === 'completed'
      });

      // API to complete
      lines.push({
        path: `M ${positions.api.x + nodeWidth} ${positions.api.y + nodeHeight/2} L ${completeX} ${completeY + nodeHeight/2}`,
        active: apiStatus === 'completed'
      });
    } else {
      // Sequential flow for salesforce orchestration
      // Start to first step
      if (stepStatuses.length > 0) {
        const firstStep = stepStatuses[0];
        lines.push({
          path: `M ${startX + nodeWidth} ${startY + nodeHeight/2} L ${positions[firstStep.step].x} ${positions[firstStep.step].y + nodeHeight/2}`,
          active: firstStep.status !== 'not-started'
        });
      }

      // Between steps
      for (let i = 0; i < stepStatuses.length - 1; i++) {
        const currentStep = stepStatuses[i];
        const nextStep = stepStatuses[i + 1];
        lines.push({
          path: `M ${positions[currentStep.step].x + nodeWidth} ${positions[currentStep.step].y + nodeHeight/2} L ${positions[nextStep.step].x} ${positions[nextStep.step].y + nodeHeight/2}`,
          active: currentStep.status === 'completed'
        });
      }

      // Last step to complete
      if (stepStatuses.length > 0) {
        const lastStep = stepStatuses[stepStatuses.length - 1];
        lines.push({
          path: `M ${positions[lastStep.step].x + nodeWidth} ${positions[lastStep.step].y + nodeHeight/2} L ${completeX} ${completeY + nodeHeight/2}`,
          active: lastStep.status === 'completed'
        });
      }
    }

    return lines;
  };

  const lines = generateLines();

  return (
    <div className="fast-track-flow-chart">
      <svg width="100%" height={config.flowChartHeight} viewBox={`0 0 ${completeX + nodeWidth + 50} ${config.flowChartHeight}`} preserveAspectRatio="xMidYMid meet">
        {/* Connecting Lines with Arrows */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" className="arrow-head" />
          </marker>
        </defs>

        {/* Render connecting lines */}
        {lines.map((line, index) => (
          <path 
            key={index}
            d={line.path} 
            className={`flow-line ${line.active ? 'active' : ''}`} 
            markerEnd="url(#arrowhead)" 
          />
        ))}

        {/* Start Node */}
        <g transform={`translate(${startX}, ${startY})`}>
          <rect width={nodeWidth} height={nodeHeight} rx="8" className="flow-node completed" />
          <text x={nodeWidth/2} y={nodeHeight/2} className="node-text">Start</text>
        </g>

        {/* Step Nodes */}
        {stepStatuses.map((stepData) => {
          const position = positions[stepData.step];
          if (!position) {
            console.warn(`No position found for step: ${stepData.step}`);
            return null;
          }

          return (
            <g key={stepData.step} transform={`translate(${position.x}, ${position.y})`}>
              <rect width={nodeWidth} height={nodeHeight} rx="8" className={`flow-node ${stepData.status}`} />
              <text x={nodeWidth/2} y={nodeHeight/2 - 5} className="node-text">
                {flowLabels[stepData.step] || stepData.step}
              </text>
              {stepData.status === 'processing' && (
                <foreignObject x={nodeWidth/2 - 20} y={nodeHeight/2 + 5} width="40" height="20">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </foreignObject>
              )}
              {stepData.status === 'completed' && (
                <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">✓</text>
              )}
              {stepData.status === 'failed' && (
                <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">✗</text>
              )}
              {stepData.status === 'stopped' && (
                <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">⚠</text>
              )}
              {stepData.status === 'skipped' && (
                <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">⊘</text>
              )}
            </g>
          );
        })}

        {/* Complete Node */}
        <g transform={`translate(${completeX}, ${completeY})`}>
          <rect width={nodeWidth} height={nodeHeight} rx="8" className={`flow-node ${finalStatus}`} />
          <text x={nodeWidth/2} y={nodeHeight/2} className="node-text">Complete</text>
          {finalStatus === 'completed' && (
            <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">✓</text>
          )}
          {finalStatus === 'failed' && (
            <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">✗</text>
          )}
          {finalStatus === 'stopped' && (
            <text x={nodeWidth/2} y={nodeHeight/2 + 20} className="node-icon">⚠</text>
          )}
        </g>
      </svg>
    </div>
  );
};

export default UnifiedOrchestrationFlowChart;
